/**
 * iter350 — Fiche travail côté atelier : consignes, heures, avancement.
 * Aucun prix, aucun total, aucun devis n'est affiché ni chargé.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  ArrowLeft, Clock, Loader2, Play, CheckCircle2, UserPlus, History, Users, Gauge,
  Package, MessageSquare, ChevronDown, Wrench, ClipboardList,
} from "lucide-react";

const dateCourte = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("fr-CH", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch { return ""; }
};
import { toast } from "sonner";
import api from "@/lib/api";
import TempsDialog from "@/components/atelier/TempsDialog";
import ConfierDialog from "@/components/atelier/ConfierDialog";
import ConsommablesDialog from "@/components/atelier/ConsommablesDialog";
import ManquantsDialog from "@/components/atelier/ManquantsDialog";
import OrFil from "@/components/or/OrFil";

const fmtMin = (min) => `${Math.floor((min || 0) / 60)} h ${String((min || 0) % 60).padStart(2, "0")}`;

const ETAT_LABEL = { a_faire: "À faire", en_travaux: "En travaux", termine: "Terminé" };
const ETAT_TONE = {
  a_faire: "bg-amber-50 text-amber-800 border-amber-200",
  en_travaux: "bg-[#52a675]/10 text-[#2d5f3f] border-[#52a675]/30",
  termine: "bg-gray-100 text-gray-700 border-gray-200",
};

export default function AtelierTravail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [tempsOpen, setTempsOpen] = useState(false);
  const [confierOpen, setConfierOpen] = useState(false);
  const [consoOpen, setConsoOpen] = useState(false);
  const [histoOuvert, setHistoOuvert] = useState(null);
  const [manquantsOpen, setManquantsOpen] = useState(false);
  const monEtatRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/atelier/travail/${id}`);
      setD(data);
    } catch {
      toast.error("Dossier introuvable");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { monEtatRef.current = d?.mon_etat || null; }, [d]);

  const action = async (quoi) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/atelier/travail/${id}/${quoi}`, {});
      setD(data);
      if (quoi === "commencer") {
        toast.success("Intervention démarrée");
        if ((data.manquants || []).length > 0) setManquantsOpen(true);
      }
      if (quoi === "terminer") {
        toast.success(
          data.statut === "travaux_effectue"
            ? "Terminé — l'administration va contrôler le dossier"
            : "Ton intervention est terminée",
        );
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail?.code === "temps_manquant") {
        toast.error(detail.message);
        setTempsOpen(true);
      } else {
        toast.error(detail?.message || detail || "Action impossible");
      }
    } finally {
      setBusy(false);
    }
  };

  const changerEtat = async (etat) => {
    if (etat === monEtatRef.current) return;
    if (etat === "termine") { action("terminer"); return; }
    if (etat === "en_travaux") { action("commencer"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/atelier/travail/${id}/prendre`, {});
      setD(data);
    } catch {
      toast.error("Action impossible");
    } finally {
      setBusy(false);
    }
  };

  const cocher = async (ligneId, fait) => {
    setD((prev) => ({
      ...prev,
      travaux: prev.travaux.map((t) => (t.id === ligneId ? { ...t, fait } : t)),
    }));
    try {
      await api.post(`/atelier/travail/${id}/travaux/${ligneId}`, { fait });
    } catch {
      toast.error("Enregistrement impossible");
      load();
    }
  };

  if (loading) {
    return <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#52a675]" /></div>;
  }
  if (!d) return null;

  const monEtat = d.mon_etat;

  return (
    <div className="p-3 sm:p-6 max-w-3xl mx-auto pb-32" data-testid="atelier-travail">
      <button
        onClick={() => navigate("/atelier/mon-poste")}
        className="text-sm text-gray-500 hover:text-[#52a675] inline-flex items-center gap-1 mb-3"
        data-testid="travail-retour"
      >
        <ArrowLeft className="w-4 h-4" /> Mon poste
      </button>

      <Card className="p-4 border-gray-200 mb-3" data-testid="travail-entete">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#52a675]/10 flex items-center justify-center flex-shrink-0">
            <Wrench className="w-5 h-5 text-[#52a675]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-xl font-bold">{d.numero}</span>
              {d.sous_statut && (
                <Badge
                  variant="outline"
                  className={d.sous_statut.ton === "attente"
                    ? "bg-orange-50 text-orange-800 border-orange-300"
                    : "bg-[#52a675]/10 text-[#2d5f3f] border-[#52a675]/30"}
                  data-testid="travail-sous-statut"
                >
                  {d.sous_statut.label}
                </Badge>
              )}
            </div>
            <div className="text-base font-semibold text-gray-900 mt-0.5">
              {d.vehicule ? `${d.vehicule.marque || ""} ${d.vehicule.modele || ""}`.trim() || d.vehicule.plaque : d.vehicule_label}
              {d.vehicule?.plaque && <span className="text-gray-400 font-normal"> · {d.vehicule.plaque}</span>}
            </div>
            <div className="text-sm text-gray-500 truncate">{d.client_nom}</div>
            <div className="text-[11px] text-gray-500 mt-1 flex items-center gap-1.5 flex-wrap">
              <Users className="w-3 h-3" />
              {d.intervenants.length === 0
                ? "Personne n'est encore sur ce dossier"
                : d.intervenants.map((i) => `${i.nom}${i.moi ? " (moi)" : ""} · ${ETAT_LABEL[i.etat]}`).join(" → ")}
            </div>
          </div>
          <div className="text-right flex-shrink-0">
            <div className="text-[10px] uppercase tracking-wider text-gray-400">Km</div>
            <div className="font-mono text-sm" data-testid="travail-km">
              {d.km_depose ? d.km_depose.toLocaleString("fr-CH") : "—"}
            </div>
          </div>
        </div>
      </Card>

      {/* iter355 — Les trois états que le collaborateur pilote lui-même */}
      <div className="grid grid-cols-3 gap-1.5 mb-3" data-testid="travail-etats">
        {[["a_faire", "À faire", ClipboardList], ["en_travaux", "En travaux", Wrench], ["termine", "Terminé", CheckCircle2]].map(
          ([key, label, Icon]) => {
            const actif = monEtat === key;
            return (
              <button
                key={key}
                onClick={() => changerEtat(key)}
                disabled={busy}
                className={`py-2.5 rounded-xl border-2 text-xs font-semibold flex flex-col items-center gap-0.5 transition-colors ${
                  actif ? "bg-[#52a675] border-[#52a675] text-white" : "bg-white border-gray-200 text-gray-600"
                }`}
                data-testid={`travail-etat-${key}`}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            );
          })}
      </div>

      {d.manquants.length > 0 && monEtat !== "a_faire" && (
        <button
          onClick={() => setManquantsOpen(true)}
          className="w-full mb-3 p-3 rounded-xl border-2 border-amber-300 bg-amber-50 text-left"
          data-testid="travail-manquants-banniere"
        >
          <div className="text-sm font-semibold text-amber-900">
            {d.manquants.length} information{d.manquants.length > 1 ? "s" : ""} à compléter
          </div>
          <div className="text-[11px] text-amber-800">
            {d.manquants.map((m) => m.label).join(" · ")}
          </div>
        </button>
      )}

      {d.notes_client && (
        <Card className="p-4 border-[#52a675]/30 bg-[#52a675]/5 mb-3" data-testid="travail-consigne">
          <div className="text-[11px] uppercase tracking-wider text-[#2d5f3f] mb-1">Consigne</div>
          <div className="text-sm whitespace-pre-wrap text-gray-900">{d.notes_client}</div>
        </Card>
      )}

      {d.travaux.length > 0 && (
        <Card className="p-4 border-gray-200 mb-3" data-testid="travail-liste">
          <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-2">
            Travaux à faire ({d.travaux.filter((t) => t.fait).length}/{d.travaux.length})
          </div>
          <div className="space-y-3">
            {[["main_oeuvre", "Main d'œuvre"], ["autre", "Pièces et fournitures"]].map(([groupe, titre]) => {
              const lignes = d.travaux.filter((t) =>
                groupe === "main_oeuvre" ? t.type_ligne === "main_oeuvre" : t.type_ligne !== "main_oeuvre");
              if (lignes.length === 0) return null;
              return (
                <div key={groupe} data-testid={`travail-groupe-${groupe}`}>
                  <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">{titre}</div>
                  <div className="space-y-2">
                    {lignes.map((t) => (
                      <label
                        key={t.id}
                        className="flex items-start gap-2.5 cursor-pointer"
                        data-testid={`travail-ligne-${t.id}`}
                      >
                        <Checkbox
                          checked={!!t.fait}
                          onCheckedChange={(v) => cocher(t.id, !!v)}
                          className="mt-0.5"
                          data-testid={`travail-check-${t.id}`}
                        />
                        <span className={`text-sm leading-snug flex-1 ${t.fait ? "line-through text-gray-400" : "text-gray-900"}`}>
                          {t.libelle}
                          {t.source === "atelier" && <span className="text-[10px] text-[#2d5f3f] ml-1">(ajouté à l'atelier)</span>}
                        </span>
                        <span className="text-xs font-mono text-gray-500 flex-shrink-0" data-testid={`travail-qte-${t.id}`}>
                          {t.type_ligne === "main_oeuvre"
                            ? `${Number(t.quantite || 0).toFixed(2)} h`
                            : `× ${Number(t.quantite || 0) % 1 === 0 ? Number(t.quantite || 0) : Number(t.quantite || 0).toFixed(2)}${t.unite && t.unite !== "piece" ? ` ${t.unite}` : ""}`}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      <Card className="p-4 border-gray-200 mb-3" data-testid="travail-intervenants">
        <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-2 flex items-center gap-1.5">
          <Users className="w-3.5 h-3.5" /> Intervenants ({d.intervenants.length}) · total {fmtMin(d.total_minutes)}
        </div>
        <div className="space-y-1.5">
          {d.intervenants.map((i) => (
            <div key={i.mecanicien_id} className="flex items-center gap-2 text-sm" data-testid={`travail-intervenant-${i.mecanicien_id}`}>
              <span className={`font-medium ${i.moi ? "text-[#2d5f3f]" : "text-gray-800"}`}>
                {i.nom}{i.moi ? " (moi)" : ""}
              </span>
              {i.service && <span className="text-[11px] text-gray-400">{i.service}</span>}
              <Badge variant="outline" className={`text-[10px] ml-auto ${ETAT_TONE[i.etat]}`}>
                {ETAT_LABEL[i.etat]}
              </Badge>
              <span className="font-mono text-xs text-gray-500 w-14 text-right">{i.heures.toFixed(2)} h</span>
            </div>
          ))}
        </div>
        {d.intervenants.some((i) => i.message_transmission) && (
          <div className="mt-3 pt-3 border-t border-gray-100 space-y-1.5">
            {d.intervenants.filter((i) => i.message_transmission).map((i) => (
              <div key={`msg-${i.mecanicien_id}`} className="text-xs text-gray-600" data-testid={`travail-transmission-${i.mecanicien_id}`}>
                <b>Pour {i.nom} :</b> {i.message_transmission}
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* iter353 — Les pièces utilisées se déclarent ici ; les demandes passent
          par un simple message marqué dans la conversation ci-dessous. */}
      <Button
        variant="outline" onClick={() => setConsoOpen(true)}
        className="h-12 w-full border-gray-300 justify-start mb-3"
        data-testid="travail-consommables-btn"
      >
        <Package className="w-4 h-4 mr-2 text-[#52a675]" /> Pièces et consommables utilisés
      </Button>

      <Card className="p-4 border-gray-200 mb-3" data-testid="travail-fil">
        <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-2 flex items-center gap-1.5">
          <MessageSquare className="w-3.5 h-3.5" /> Échanges avec l'administration
        </div>
        <OrFil orId={id} compact peutDemander />
      </Card>

      {d.historique_vehicule.length > 0 && (
        <Card className="p-4 border-gray-200 mb-3" data-testid="travail-historique">
          <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-2 flex items-center gap-1.5">
            <History className="w-3.5 h-3.5" /> Passages précédents
          </div>
          <div className="divide-y divide-gray-100">
            {d.historique_vehicule.map((h) => {
              const ouvert = histoOuvert === h.id;
              return (
                <div key={h.id} className="py-2" data-testid={`travail-histo-${h.id}`}>
                  <button
                    onClick={() => setHistoOuvert(ouvert ? null : h.id)}
                    className="w-full flex items-center gap-2 text-left"
                    data-testid={`travail-histo-toggle-${h.id}`}
                  >
                    <span className="font-mono text-xs font-bold text-gray-700">{h.numero}</span>
                    <span className="text-[11px] text-gray-500">{dateCourte(h.date)}</span>
                    {h.km ? (
                      <span className="text-[11px] text-gray-400 inline-flex items-center gap-0.5">
                        <Gauge className="w-3 h-3" />{h.km.toLocaleString("fr-CH")}
                      </span>
                    ) : null}
                    <span className="text-[11px] text-gray-400 ml-auto">
                      {h.travaux.length} ligne{h.travaux.length > 1 ? "s" : ""}
                    </span>
                    <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${ouvert ? "rotate-180" : ""}`} />
                  </button>
                  {ouvert && (
                    <div className="mt-2 pl-1 space-y-1" data-testid={`travail-histo-detail-${h.id}`}>
                      {h.demande && (
                        <div className="text-[11px] text-gray-500 italic">Demande : {h.demande}</div>
                      )}
                      {h.travaux.length === 0 && (
                        <div className="text-[11px] text-gray-400">Aucun travail enregistré sur ce passage</div>
                      )}
                      {h.travaux.map((t, i) => (
                        <div key={i} className="flex items-start gap-2 text-xs">
                          <span className="text-gray-800 flex-1">{t.libelle}</span>
                          <span className="font-mono text-gray-500 flex-shrink-0">
                            {t.type_ligne === "main_oeuvre"
                              ? `${Number(t.quantite || 0).toFixed(2)} h`
                              : `× ${Number(t.quantite || 0) % 1 === 0 ? Number(t.quantite || 0) : Number(t.quantite || 0).toFixed(2)}`}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Barre d'actions fixe — pensée pour le pouce */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-3 pr-20 sm:pr-3 lg:pl-[17rem] z-20" data-testid="travail-actions">
        <div className="max-w-3xl mx-auto flex gap-2">
          <Button
            variant="outline" onClick={() => setTempsOpen(true)}
            className="h-12 flex-1 border-gray-300"
            data-testid="travail-temps-btn"
          >
            <Clock className="w-4 h-4 mr-1.5" />
            {d.mes_minutes ? fmtMin(d.mes_minutes) : "Temps passé"}
          </Button>
          {monEtat !== "en_travaux" && monEtat !== "termine" && (
            <Button
              onClick={() => action("commencer")} disabled={busy}
              className="h-12 flex-1 bg-[#52a675] hover:bg-[#2d5f3f]"
              data-testid="travail-commencer-btn"
            >
              <Play className="w-4 h-4 mr-1.5" /> Commencer
            </Button>
          )}
          {monEtat === "en_travaux" && (
            <>
              <Button
                variant="outline" onClick={() => setConfierOpen(true)} disabled={busy}
                className="h-12 border-gray-300"
                data-testid="travail-confier-btn"
              >
                <UserPlus className="w-4 h-4" />
              </Button>
              <Button
                onClick={() => action("terminer")} disabled={busy}
                className="h-12 flex-1 bg-[#52a675] hover:bg-[#2d5f3f]"
                data-testid="travail-terminer-btn"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4 mr-1.5" /> Terminer</>}
              </Button>
            </>
          )}
          {monEtat === "termine" && (
            <Button
              variant="outline" onClick={() => setConfierOpen(true)}
              className="h-12 flex-1 border-gray-300"
              data-testid="travail-confier-btn"
            >
              <UserPlus className="w-4 h-4 mr-1.5" /> Confier à un collègue
            </Button>
          )}
        </div>
      </div>

      <TempsDialog open={tempsOpen} onClose={() => setTempsOpen(false)} orId={id} onSaved={load} />
      <ConfierDialog open={confierOpen} onClose={() => setConfierOpen(false)} orId={id} onDone={load} />
      <ManquantsDialog
        open={manquantsOpen} onClose={() => setManquantsOpen(false)}
        orId={id} manquants={d.manquants} onSaved={setD}
      />
      <ConsommablesDialog open={consoOpen} onClose={() => setConsoOpen(false)} orId={id} onDone={load} />
    </div>
  );
}
